import type { UserRoleString } from "@nlc/shared-types";

import { USER_ROLES } from "@nlc/shared-types";
import { useMutation } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import apiClient from "@/lib/api/client";

const handleCsvDownload = async (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: "text/csv" });
  const blobUrl = window.URL.createObjectURL(blob);

  const a = document.createElement("a");

  a.href = blobUrl;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
};

export function useExportProjectsCsv() {
  const { hasRole } = useRoleAccess();

  const mutation = useMutation({
    mutationFn: async () => {
      const filename = `active-accounts-${new Date().toISOString().split("T")[0]}.csv`;

      const response = await apiClient
        .url("/projects/export/active-accounts")
        .get()
        .res();

      if (!response.ok) {
        throw new Error("Failed to export accounts");
      }

      const csvContent = await response.text();

      await handleCsvDownload(csvContent, filename);

      return { success: true };
    },
    onError: (error) => {
      console.error("Export failed:", error);
      toast.add({
        description:
          "There was an error exporting the accounts. Please try again.",
        title: "Error exporting accounts",
        type: "error",
      });
    },
  });

  const canExport = hasRole([USER_ROLES.SUPER_ADMIN as UserRoleString]);

  return {
    canExport,
    exportCsv: mutation.mutate,
    isExporting: mutation.isPending,
  };
}
